import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  DollarSign, TrendingUp, TrendingDown, CreditCard, Users, Package, 
  FileText, Download, RefreshCw, Calendar, ArrowUpRight, ArrowDownRight,
  Loader2, Receipt, Percent, ShoppingCart, Building, Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useCurrency } from '@/lib/currency-context';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import api from '@/lib/api';

// Simple Chart Component
const SimpleBarChart = ({ data, dataKey, labelKey, color = '#ef4444', height = 200 }) => {
  if (!data || data.length === 0) return <div className="h-[200px] flex items-center justify-center text-muted-foreground">Veri yok</div>;
  
  const maxValue = Math.max(...data.map(d => d[dataKey] || 0));
  
  return (
    <div className="flex items-end gap-1 h-[200px] w-full overflow-x-auto pb-6 pt-4">
      {data.map((item, index) => {
        const value = item[dataKey] || 0;
        const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
        return (
          <div key={index} className="flex flex-col items-center flex-1 min-w-[20px] group relative">
            <div 
              className="w-full bg-primary/20 rounded-t transition-all hover:bg-primary/30"
              style={{ 
                height: `${Math.max(percentage, 2)}%`,
                backgroundColor: color,
                opacity: 0.7
              }}
            />
            <span className="text-[10px] text-muted-foreground mt-1 -rotate-45 origin-left whitespace-nowrap">
              {item[labelKey]?.slice(-5) || ''}
            </span>
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-popover text-popover-foreground px-2 py-1 rounded text-xs shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
              {value.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const FinanceDashboardPage = () => {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [commissions, setCommissions] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [chartPeriod, setChartPeriod] = useState('daily');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, transactionsRes, invoicesRes, commissionsRes, refundsRes] = await Promise.all([
        api.get('/finance/dashboard'),
        api.get('/finance/transactions?limit=100'),
        api.get('/finance/invoices?limit=50'),
        api.get('/finance/commissions'),
        api.get('/finance/refunds?limit=50')
      ]);
      
      setDashboard(dashboardRes.data);
      setTransactions(transactionsRes.data.transactions || []);
      setInvoices(invoicesRes.data.invoices || []);
      setCommissions(commissionsRes.data);
      setRefunds(refundsRes.data.refunds || []);
    } catch (error) {
      console.error('Finance data error:', error);
      toast.error('Finans verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const generateInvoice = async (orderId) => {
    try {
      const res = await api.post('/finance/invoices/generate', { order_id: orderId });
      toast.success('Fatura oluşturuldu');
      fetchAllData();
    } catch (error) {
      toast.error('Fatura oluşturulamadı');
    }
  };

  const exportToPDF = (invoice) => {
    // Create printable invoice
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Fatura - ${invoice.invoice_number}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { display: flex; justify-content: space-between; border-bottom: 2px solid #ef4444; padding-bottom: 20px; margin-bottom: 30px; }
          .logo { font-size: 24px; font-weight: bold; color: #ef4444; }
          .invoice-info { text-align: right; }
          .invoice-number { font-size: 20px; font-weight: bold; }
          .section { margin-bottom: 30px; }
          .section-title { font-weight: bold; margin-bottom: 10px; color: #333; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background: #f5f5f5; }
          .totals { margin-top: 30px; text-align: right; }
          .total-row { display: flex; justify-content: flex-end; gap: 50px; padding: 5px 0; }
          .grand-total { font-size: 18px; font-weight: bold; border-top: 2px solid #333; padding-top: 10px; }
          .footer { margin-top: 50px; text-align: center; color: #666; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">🇹🇷 Turkish E-Market</div>
          <div class="invoice-info">
            <div class="invoice-number">${invoice.invoice_number}</div>
            <div>Tarih: ${new Date(invoice.created_at).toLocaleDateString('tr-TR')}</div>
            <div>Vade: ${new Date(invoice.due_date).toLocaleDateString('tr-TR')}</div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Müşteri Bilgileri</div>
          <div>${invoice.buyer?.name || 'N/A'}</div>
          <div>${invoice.buyer?.email || ''}</div>
          <div>${invoice.buyer?.address?.address || ''}</div>
          <div>${invoice.buyer?.address?.city || ''} ${invoice.buyer?.address?.district || ''}</div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Ürün</th>
              <th>Adet</th>
              <th>Birim Fiyat</th>
              <th>Toplam</th>
            </tr>
          </thead>
          <tbody>
            ${(invoice.items || []).map(item => `
              <tr>
                <td>${item.name || 'Ürün'}</td>
                <td>${item.quantity || 1}</td>
                <td>${(item.price || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</td>
                <td>${((item.price || 0) * (item.quantity || 1)).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <div class="total-row"><span>Ara Toplam:</span> <span>${(invoice.subtotal || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</span></div>
          <div class="total-row"><span>Kargo:</span> <span>${(invoice.shipping || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</span></div>
          <div class="total-row"><span>KDV:</span> <span>${(invoice.tax || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</span></div>
          <div class="total-row grand-total"><span>Genel Toplam:</span> <span>${(invoice.total || 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}</span></div>
        </div>
        
        <div class="footer">
          <p>Turkish E-Market - Türkiye'nin Online Pazaryeri</p>
          <p>Bu fatura elektronik olarak oluşturulmuştur.</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const summary = dashboard?.summary || {};
  const chartData = chartPeriod === 'daily' ? dashboard?.daily_revenue : dashboard?.monthly_revenue;

  return (
    <div className="min-h-screen bg-background" data-testid="finance-dashboard">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <DollarSign className="w-8 h-8 text-primary" />
              {t('finance.title') || 'Finans Yönetimi'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('finance.description') || 'Gelir, gider ve komisyon raporları'}
            </p>
          </div>
          <Button onClick={fetchAllData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            {t('common.refresh') || 'Yenile'}
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('finance.totalRevenue') || 'Toplam Gelir'}</p>
                  <p className="text-2xl font-bold text-green-600">{formatPrice(summary.total_revenue || 0)}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {summary.total_orders || 0} sipariş
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('finance.monthRevenue') || 'Bu Ay'}</p>
                  <p className="text-2xl font-bold text-blue-600">{formatPrice(summary.month_revenue || 0)}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <ArrowUpRight className="w-3 h-3 text-green-500" />
                Bugün: {formatPrice(summary.today_revenue || 0)}
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('finance.commission') || 'Komisyon'}</p>
                  <p className="text-2xl font-bold text-yellow-600">{formatPrice(summary.total_commission || 0)}</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <Percent className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                %{summary.commission_rate || 10} oran
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('finance.pending') || 'Bekleyen'}</p>
                  <p className="text-2xl font-bold text-red-600">{formatPrice(summary.pending_amount || 0)}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <CreditCard className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {summary.pending_orders || 0} bekleyen sipariş
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="gap-2">
              <TrendingUp className="w-4 h-4" />
              {t('finance.overview') || 'Genel Bakış'}
            </TabsTrigger>
            <TabsTrigger value="transactions" className="gap-2">
              <CreditCard className="w-4 h-4" />
              {t('finance.transactions') || 'İşlemler'}
            </TabsTrigger>
            <TabsTrigger value="invoices" className="gap-2">
              <FileText className="w-4 h-4" />
              {t('finance.invoices') || 'Faturalar'}
            </TabsTrigger>
            <TabsTrigger value="commissions" className="gap-2">
              <Percent className="w-4 h-4" />
              {t('finance.commissions') || 'Komisyonlar'}
            </TabsTrigger>
            <TabsTrigger value="refunds" className="gap-2">
              <TrendingDown className="w-4 h-4" />
              {t('finance.refunds') || 'İadeler'}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Chart */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{t('finance.revenueChart') || 'Gelir Grafiği'}</CardTitle>
                    <Select value={chartPeriod} onValueChange={setChartPeriod}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Günlük</SelectItem>
                        <SelectItem value="monthly">Aylık</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent>
                  <SimpleBarChart 
                    data={chartData} 
                    dataKey="revenue" 
                    labelKey={chartPeriod === 'daily' ? 'date' : 'month'}
                    color="#ef4444"
                  />
                </CardContent>
              </Card>

              {/* Top Products */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('finance.topProducts') || 'En Çok Satan Ürünler'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {(dashboard?.top_products || []).slice(0, 5).map((product, index) => (
                      <div key={product.product_id} className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </span>
                          <div>
                            <p className="font-medium text-sm">{product.name}</p>
                            <p className="text-xs text-muted-foreground">{product.quantity} adet satıldı</p>
                          </div>
                        </div>
                        <span className="font-semibold text-green-600">{formatPrice(product.revenue)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Seller Breakdown */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="w-5 h-5" />
                    {t('finance.sellerBreakdown') || 'Satıcı Bazlı Gelir'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('finance.seller') || 'Satıcı'}</TableHead>
                        <TableHead className="text-right">{t('finance.orders') || 'Sipariş'}</TableHead>
                        <TableHead className="text-right">{t('finance.revenue') || 'Gelir'}</TableHead>
                        <TableHead className="text-right">{t('finance.commission') || 'Komisyon'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(dashboard?.seller_breakdown || []).map((seller) => (
                        <TableRow key={seller.seller_id}>
                          <TableCell className="font-medium">{seller.seller_name}</TableCell>
                          <TableCell className="text-right">{seller.orders}</TableCell>
                          <TableCell className="text-right text-green-600">{formatPrice(seller.revenue)}</TableCell>
                          <TableCell className="text-right text-yellow-600">{formatPrice(seller.commission)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>{t('finance.paymentTransactions') || 'Ödeme İşlemleri'}</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>{t('finance.order') || 'Sipariş'}</TableHead>
                      <TableHead>{t('finance.amount') || 'Tutar'}</TableHead>
                      <TableHead>{t('finance.status') || 'Durum'}</TableHead>
                      <TableHead>{t('finance.date') || 'Tarih'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          Henüz işlem yok
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell className="font-mono text-sm">{tx.id?.slice(0, 8)}...</TableCell>
                          <TableCell className="font-mono text-sm">{tx.order_id?.slice(0, 8)}...</TableCell>
                          <TableCell className="font-semibold">{formatPrice(tx.amount || 0)}</TableCell>
                          <TableCell>
                            <Badge variant={tx.payment_status === 'paid' ? 'default' : tx.payment_status === 'initiated' ? 'secondary' : 'destructive'}>
                              {tx.payment_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{tx.created_at ? formatDate(tx.created_at) : 'N/A'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  {t('finance.invoiceList') || 'Fatura Listesi'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('finance.invoiceNo') || 'Fatura No'}</TableHead>
                      <TableHead>{t('finance.customer') || 'Müşteri'}</TableHead>
                      <TableHead>{t('finance.amount') || 'Tutar'}</TableHead>
                      <TableHead>{t('finance.status') || 'Durum'}</TableHead>
                      <TableHead>{t('finance.date') || 'Tarih'}</TableHead>
                      <TableHead className="text-right">{t('common.actions') || 'İşlemler'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          Henüz fatura yok
                        </TableCell>
                      </TableRow>
                    ) : (
                      invoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-mono font-medium">{invoice.invoice_number}</TableCell>
                          <TableCell>{invoice.buyer?.name || 'N/A'}</TableCell>
                          <TableCell className="font-semibold">{formatPrice(invoice.total || 0)}</TableCell>
                          <TableCell>
                            <Badge variant={invoice.payment_status === 'paid' ? 'default' : 'secondary'}>
                              {invoice.payment_status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{invoice.created_at ? formatDate(invoice.created_at) : 'N/A'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="icon" onClick={() => setSelectedInvoice(invoice)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => exportToPDF(invoice)}>
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Commissions Tab */}
          <TabsContent value="commissions">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">{t('finance.commissionRate') || 'Komisyon Oranı'}</p>
                    <p className="text-3xl font-bold text-primary">%{commissions?.commission_rate || 10}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">{t('finance.totalSales') || 'Toplam Satış'}</p>
                    <p className="text-3xl font-bold text-green-600">{formatPrice(commissions?.total_sales || 0)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">{t('finance.platformCommission') || 'Platform Komisyonu'}</p>
                    <p className="text-3xl font-bold text-yellow-600">{formatPrice(commissions?.total_platform_commission || 0)}</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{t('finance.sellerCommissions') || 'Satıcı Komisyonları'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('finance.seller') || 'Satıcı'}</TableHead>
                        <TableHead>{t('finance.email') || 'E-posta'}</TableHead>
                        <TableHead className="text-right">{t('finance.orders') || 'Sipariş'}</TableHead>
                        <TableHead className="text-right">{t('finance.totalSales') || 'Toplam Satış'}</TableHead>
                        <TableHead className="text-right">{t('finance.commission') || 'Komisyon'}</TableHead>
                        <TableHead className="text-right">{t('finance.netEarnings') || 'Net Kazanç'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(commissions?.sellers || []).map((seller) => (
                        <TableRow key={seller.seller_id}>
                          <TableCell className="font-medium">{seller.seller_name}</TableCell>
                          <TableCell className="text-muted-foreground">{seller.email}</TableCell>
                          <TableCell className="text-right">{seller.orders_count}</TableCell>
                          <TableCell className="text-right">{formatPrice(seller.total_sales)}</TableCell>
                          <TableCell className="text-right text-yellow-600">{formatPrice(seller.commission_amount)}</TableCell>
                          <TableCell className="text-right text-green-600 font-semibold">{formatPrice(seller.net_earnings)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Refunds Tab */}
          <TabsContent value="refunds">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  {t('finance.refundList') || 'İade Listesi'}
                </CardTitle>
                <CardDescription>
                  Toplam İade: {formatPrice(summary.total_refunds || 0)}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>ID</TableHead>
                      <TableHead>{t('finance.order') || 'Sipariş'}</TableHead>
                      <TableHead>{t('finance.amount') || 'Tutar'}</TableHead>
                      <TableHead>{t('finance.reason') || 'Sebep'}</TableHead>
                      <TableHead>{t('finance.status') || 'Durum'}</TableHead>
                      <TableHead>{t('finance.date') || 'Tarih'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {refunds.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          Henüz iade yok
                        </TableCell>
                      </TableRow>
                    ) : (
                      refunds.map((refund) => (
                        <TableRow key={refund.id}>
                          <TableCell className="font-mono text-sm">{refund.id?.slice(0, 8)}...</TableCell>
                          <TableCell className="font-mono text-sm">{refund.order_id?.slice(0, 8)}...</TableCell>
                          <TableCell className="font-semibold text-red-600">{formatPrice(refund.amount || 0)}</TableCell>
                          <TableCell>{refund.reason}</TableCell>
                          <TableCell>
                            <Badge variant={refund.status === 'processed' ? 'default' : 'secondary'}>
                              {refund.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{refund.created_at ? formatDate(refund.created_at) : 'N/A'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Invoice Detail Dialog */}
        <Dialog open={!!selectedInvoice} onOpenChange={() => setSelectedInvoice(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Fatura: {selectedInvoice?.invoice_number}
              </DialogTitle>
              <DialogDescription>
                Oluşturulma: {selectedInvoice?.created_at ? formatDate(selectedInvoice.created_at) : 'N/A'}
              </DialogDescription>
            </DialogHeader>
            
            {selectedInvoice && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold mb-2">Müşteri</h4>
                    <p>{selectedInvoice.buyer?.name}</p>
                    <p className="text-sm text-muted-foreground">{selectedInvoice.buyer?.email}</p>
                  </div>
                  <div className="text-right">
                    <h4 className="font-semibold mb-2">Durum</h4>
                    <Badge variant={selectedInvoice.payment_status === 'paid' ? 'default' : 'secondary'}>
                      {selectedInvoice.payment_status}
                    </Badge>
                  </div>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ürün</TableHead>
                      <TableHead className="text-right">Adet</TableHead>
                      <TableHead className="text-right">Fiyat</TableHead>
                      <TableHead className="text-right">Toplam</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedInvoice.items || []).map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell className="text-right">{formatPrice(item.price)}</TableCell>
                        <TableCell className="text-right">{formatPrice(item.price * item.quantity)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span>Ara Toplam</span>
                    <span>{formatPrice(selectedInvoice.subtotal || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Kargo</span>
                    <span>{formatPrice(selectedInvoice.shipping || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>KDV</span>
                    <span>{formatPrice(selectedInvoice.tax || 0)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Genel Toplam</span>
                    <span>{formatPrice(selectedInvoice.total || 0)}</span>
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <Button onClick={() => exportToPDF(selectedInvoice)} className="gap-2">
                    <Download className="w-4 h-4" />
                    PDF İndir
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default FinanceDashboardPage;
